/**
 * Hook pour validation email avec vérification doublons
 * Utilisé dans TeamManagement pour validation temps réel
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { validateEmail, normalizeEmail, type EmailValidationResult } from '@/utils/emailValidation';

interface EmailValidationState {
  isValid: boolean;
  error: string;
  isChecking: boolean;
  warnings: string[];
}

interface UseEmailValidationOptions {
  organizationId?: string;
  debounceMs?: number;
  checkDuplicates?: boolean;
}

export function useEmailValidation(
  email: string,
  options: UseEmailValidationOptions = {}
) {
  const {
    organizationId,
    debounceMs = 300,
    checkDuplicates = true
  } = options;

  const [validation, setValidation] = useState<EmailValidationState>({
    isValid: false,
    error: '',
    isChecking: false,
    warnings: []
  });

  /**
   * Vérifie si l'email existe déjà comme membre de l'organisation
   */
  const checkExistingMember = useCallback(async (emailToCheck: string): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('email', normalizeEmail(emailToCheck))
        .maybeSingle();

      if (error) {
        console.warn('Erreur vérification membre existant:', error);
        return false;
      }

      return !!data;
    } catch (_error) {
      console.warn('Erreur vérification membre existant:', _error);
      return false;
    }
  }, [organizationId]);

  /**
   * Vérifie si une invitation est déjà en attente pour cet email
   */
  const checkPendingInvitation = useCallback(async (emailToCheck: string): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      const { data, error } = await supabase
        .from('organization_invitations')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('email', normalizeEmail(emailToCheck))
        .eq('status', 'pending')
        .maybeSingle();

      if (error) {
        console.warn('Erreur vérification invitation en attente:', error);
        return false;
      }

      return !!data;
    } catch (_error) {
      console.warn('Erreur vérification invitation en attente:', _error);
      return false;
    }
  }, [organizationId]);

  /**
   * Validation complète avec vérification doublons
   */
  const validateEmailComplete = useCallback(async (emailToValidate: string): Promise<EmailValidationResult> => {
    // 1. Validation format et domaine
    const basicValidation = validateEmail(emailToValidate);
    if (!basicValidation.isValid) {
      return basicValidation;
    }

    // 2. Vérification doublons si activée
    if (checkDuplicates && organizationId) {
      const [isExistingMember, hasPendingInvitation] = await Promise.all([
        checkExistingMember(emailToValidate),
        checkPendingInvitation(emailToValidate)
      ]);

      if (isExistingMember) {
        return {
          isValid: false,
          error: 'Cette personne est déjà membre de votre organisation'
        };
      }

      if (hasPendingInvitation) {
        return {
          isValid: false,
          error: 'Une invitation est déjà en attente pour cette adresse'
        };
      }
    }

    return { isValid: true };
  }, [organizationId, checkDuplicates, checkExistingMember, checkPendingInvitation]);

  /**
   * Effet de validation avec debounce
   */
  useEffect(() => {
    // Reset si email vide
    if (!email.trim()) {
      setValidation({
        isValid: false,
        error: '',
        isChecking: false,
        warnings: []
      });
      return;
    }

    // Démarrer la vérification
    setValidation(prev => ({ ...prev, isChecking: true, error: '' }));

    const timeoutId = setTimeout(async () => {
      try {
        const result = await validateEmailComplete(email);
        
        setValidation({
          isValid: result.isValid,
          error: result.error || '',
          isChecking: false,
          warnings: result.warnings || []
        });
      } catch (_error) {
        console.error('Erreur validation email:', _error);
        setValidation({
          isValid: false,
          error: 'Erreur lors de la validation',
          isChecking: false,
          warnings: []
        });
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [email, validateEmailComplete, debounceMs]);

  /**
   * Validation manuelle (pour forcer une re-validation)
   */
  const revalidate = useCallback(async () => {
    if (!email.trim()) return;

    setValidation(prev => ({ ...prev, isChecking: true, error: '' }));

    try {
      const result = await validateEmailComplete(email);
      
      setValidation({
        isValid: result.isValid,
        error: result.error || '',
        isChecking: false,
        warnings: result.warnings || []
      });
    } catch (_error) {
      console.error('Erreur validation email:', _error);
      setValidation({
        isValid: false,
        error: 'Erreur lors de la validation',
        isChecking: false,
        warnings: []
      });
    }
  }, [email, validateEmailComplete]);

  return {
    ...validation,
    revalidate
  };
}